import { Card } from "@blueprintjs/core";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import { Box } from "@/components/layout/flexbox";
import { CumulativeData } from "@/components/pages_public/sales-comp/utils/types";
import { LIGHT_BACKGROUND, MIDTEXT_COLOR } from "@/css/constants";

interface BreakEvenChartProps {
  breakEvenMonth: number | null;
  cumulativeData: CumulativeData[];
}

// Custom tooltip component
function CustomTooltip({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType>) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: "white",
        border: "1px solid #ccc",
        borderRadius: "4px",
        padding: "10px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>Month {label}</p>
      {payload.map((entry, index) => (
        <p
          key={`item-${index}`}
          style={{
            margin: "2px 0",
            color: entry.color,
            fontSize: "14px",
          }}
        >
          <span style={{ fontWeight: "bold" }}>{entry.name}: </span>
          <span>${Math.round(entry.value as number).toLocaleString()}</span>
        </p>
      ))}
    </div>
  );
}

function BreakEvenChart({
  breakEvenMonth,
  cumulativeData,
}: BreakEvenChartProps) {
  return (
    <Box px={20}>
      <Card
        elevation={0}
        style={{
          marginTop: "48px",
          padding: "24px",
          backgroundColor: LIGHT_BACKGROUND,
        }}
      >
        <Box
          as="h2"
          mb={4}
          fontSize="18px"
          fontWeight={600}
          color={MIDTEXT_COLOR}
        >
          Investment Return Analysis
        </Box>

        <Card
          style={{
            marginBottom: "16px",
            padding: "16px",
            textAlign: "center",
          }}
        >
          {breakEvenMonth ? (
            <Box>
              <Box fontSize="18px" fontWeight={500} color="#1F2937">
                Break-even occurs in Month {breakEvenMonth}
              </Box>
              <Box mt={1} fontSize="14px" color={MIDTEXT_COLOR}>
                At this point, cumulative revenue equals or exceeds cumulative
                cost (salary + commission)
              </Box>
            </Box>
          ) : (
            <Box>
              <Box fontSize="18px" fontWeight={500} color="#D97706">
                Break-even not reached within 12 months
              </Box>
              <Box mt={1} fontSize="14px" color="#D97706">
                Consider adjusting performance or compensation structure to
                reach profitability sooner
              </Box>
            </Box>
          )}
        </Card>

        <Card
          style={{
            height: "400px",
            padding: "16px",
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={cumulativeData}
              margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11 }}
                label={{
                  value: "Month",
                  position: "insideBottomRight",
                  offset: -10,
                  style: { fontSize: 12 },
                }}
              />
              <YAxis
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                tick={{ fontSize: 11 }}
                label={{
                  value: "Amount ($)",
                  angle: -90,
                  position: "insideLeft",
                  offset: -20,
                  style: { fontSize: 12 },
                }}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="cumulativeCost"
                name="Cumulative Cost"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="cumulativeRevenue"
                name="Cumulative Revenue"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="netValue"
                name="Net Value"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              {breakEvenMonth && (
                <ReferenceLine
                  x={breakEvenMonth}
                  stroke="#6366f1"
                  strokeWidth={2}
                  strokeDasharray="3 3"
                  label={{
                    value: "Break-even",
                    position: "top",
                    fill: "#6366f1",
                    fontSize: 12,
                  }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </Card>
    </Box>
  );
}

export default BreakEvenChart;
